// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// HealthEncyclopediaApiError. Activated by setting VITE_USE_MOCK_API=false;
// see health-encyclopedia.adapter.ts. Contract: docs/api-contracts/health-encyclopedia.md.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  HealthEncyclopediaApiError,
  isHealthEncyclopediaApiErrorCode,
  type Category,
  type CategoryListResult,
  type CategoryNameInput,
  type ContentDetail,
  type ContentListParams,
  type ContentListResult,
  type CreateContentInput,
  type HealthEncyclopediaApi,
  type UpdateContentInput,
  type UploadResult,
} from '../types/health-encyclopedia.types';

const base = endpoints.healthEncyclopedia;
const contentsPath = `${base}/contents`;
const categoriesPath = `${base}/categories`;
const uploadsPath = `${base}/uploads`;

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

// SHCT-712 — an unrecognized server code (`INTERNAL`, `SERVICE_UNAVAILABLE`, …) is a
// System Error, so it collapses to `UNKNOWN` AND drops the server's `message`. Keeping
// the raw text leaked internals verbatim to the operator ("DB connection pool exhausted
// at HikariPool-1"); PRD 10 §8.2 / 21 §7.2 require the fixed per-operation copy instead.
// A KNOWN business/validation code still carries its server message, since that copy is
// curated and feeds the i18n fallback chain (CLAUDE.md §7.1 rule 4).
function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  const rawCode = payload?.code;
  if (isHealthEncyclopediaApiErrorCode(rawCode)) {
    throw new HealthEncyclopediaApiError(
      rawCode,
      payload?.message ?? '알 수 없는 오류가 발생했습니다.',
      { fields: payload?.fields, meta: payload?.meta },
    );
  }
  throw new HealthEncyclopediaApiError('UNKNOWN', '알 수 없는 오류가 발생했습니다.', {
    fields: payload?.fields,
    meta: payload?.meta,
  });
}

// Serializes ContentListParams → the query string described in contract §3.1.
function toQuery(params: ContentListParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    page: params.page,
    size: params.size,
    sort: `${params.sortKey},${params.sortDir}`,
  };
  if (params.categoryId !== undefined) query.categoryId = params.categoryId;
  if (params.q) query.q = params.q;
  return query;
}

export const healthEncyclopediaHttpApi: HealthEncyclopediaApi = {
  async listContents(params: ContentListParams): Promise<ContentListResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<ContentListResult>>(contentsPath, {
        params: toQuery(params),
      });
      const data = res.data?.data;
      // `content` is iterated by the list table; a wrong shape (non-array, HTML,
      // bare array without the paged wrapper) must surface as a load error.
      if (!data || !Array.isArray(data.content)) {
        throw new HealthEncyclopediaApiError('UNKNOWN', '목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getContent(eid: string): Promise<ContentDetail> {
    try {
      const res = await httpClient.get<ApiEnvelope<ContentDetail>>(`${contentsPath}/${eid}`);
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new HealthEncyclopediaApiError('UNKNOWN', '콘텐츠를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async createContent(input: CreateContentInput): Promise<ContentDetail> {
    try {
      const res = await httpClient.post<ApiEnvelope<ContentDetail>>(contentsPath, input);
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new HealthEncyclopediaApiError('UNKNOWN', '콘텐츠를 저장할 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateContent(eid: string, input: UpdateContentInput): Promise<ContentDetail> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ContentDetail>>(
        `${contentsPath}/${eid}`,
        input,
      );
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new HealthEncyclopediaApiError('UNKNOWN', '콘텐츠를 저장할 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteContent(eid: string): Promise<void> {
    try {
      await httpClient.delete(`${contentsPath}/${eid}`);
    } catch (e) {
      mapError(e);
    }
  },

  // PRD §3.9 — home-exposure toggle. The BE rejects a DRAFT target
  // (HOME_EXPOSE_NOT_PUBLISHED) and a 4th ON (HOME_EXPOSE_LIMIT_EXCEEDED); both
  // surface as HealthEncyclopediaApiError and are shown in a modal.
  async setHomeExpose(eid: string, homeExposed: boolean): Promise<ContentDetail> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ContentDetail>>(
        `${contentsPath}/${eid}/home-expose`,
        { homeExposed },
      );
      const data = res.data?.data;
      // The response reconciles the row; a missing flag would leave the toggle
      // showing a state the server never confirmed.
      if (!data || typeof data.homeExposed !== 'boolean') {
        throw new HealthEncyclopediaApiError('UNKNOWN', '홈 노출 설정을 변경할 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async listCategories(): Promise<CategoryListResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<CategoryListResult>>(categoriesPath);
      const data = res.data?.data;
      // `categories` is iterated by the category filter/manager; a wrong shape
      // must surface as a load error.
      if (!data || !Array.isArray(data.categories)) {
        throw new HealthEncyclopediaApiError('UNKNOWN', '카테고리를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async createCategory(input: CategoryNameInput): Promise<Category> {
    try {
      const res = await httpClient.post<ApiEnvelope<Category>>(categoriesPath, input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateCategory(id: number, input: CategoryNameInput): Promise<Category> {
    try {
      const res = await httpClient.patch<ApiEnvelope<Category>>(
        `${categoriesPath}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteCategory(id: number): Promise<void> {
    try {
      await httpClient.delete(`${categoriesPath}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async uploadImage(file: File): Promise<UploadResult> {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await httpClient.post<ApiEnvelope<UploadResult>>(uploadsPath, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },
};
